import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, Transition} from '@headlessui/react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {ChevronDown, Loader2, Pencil, Plus, Trash2} from 'lucide-react';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import {useTenant} from '@/shared/hooks/useTenant';
import AdminService from '@/modules/admin/services/adminService';
import type {
    AssayCatalogItem,
    OrgReferenceRange,
    OrgReferenceRangeInput,
    RefRangeSex,
    RefRangeMenopausalStatus,
    RefRangeCyclePhase,
} from '@/shared/types/api';

interface Props {
    orgId?: number | null;
}

/**
 * Range entry uses an implicit-format model: two free-text inputs.  The
 * format is derived from which fields are populated.
 *   - Both filled        → range  "<min>-<max>"
 *   - Only `min` filled  → greater-than  ">min"
 *   - Only `max` filled  → less-than  "<max>"
 *   - Neither filled     → invalid (caller surfaces a toast)
 *
 * Strings (not numbers) so the input retains typing-state like the
 * trailing decimal point in "5.".
 */
interface RangeFields {
    min: string;
    max: string;
}

/** Parse a stored `ref_range` string back into Min/Max fields. */
function parseRefRange(raw: string): RangeFields {
    const s = raw.trim();
    const rangeMatch = s.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)$/);
    if (rangeMatch) return {min: rangeMatch[1], max: rangeMatch[2]};
    const ltMatch = s.match(/^<\s*(\d+\.?\d*)$/);
    if (ltMatch) return {min: '', max: ltMatch[1]};
    const gtMatch = s.match(/^>\s*(\d+\.?\d*)$/);
    if (gtMatch) return {min: gtMatch[1], max: ''};
    return {min: '', max: ''};
}

/** Build a `ref_range` string from the Min/Max fields. */
function buildRefRange(fields: RangeFields): string {
    const min = fields.min.trim();
    const max = fields.max.trim();
    if (min && max) return `${min}-${max}`;
    if (max) return `<${max}`;
    if (min) return `>${min}`;
    return '';
}

const EMPTY_RANGE: RangeFields = {min: '', max: ''};

/**
 * Live preview rendered below the Min/Max inputs.  Shows how the values
 * will be interpreted as a normal-range condition — so the admin can see
 * the math before saving instead of having to read the help text and
 * imagine it.  Switches between three modes:
 *   - empty       → static help text
 *   - valid       → "Normal: result > 105" / "< 5.7" / "75 – 100"
 *   - min ≥ max   → error hint in rose
 */
function RangePreview({fields}: {fields: RangeFields}) {
    const min = fields.min.trim();
    const max = fields.max.trim();

    if (!min && !max) {
        return (
            <p className="mt-1.5 text-xs text-mld-muted-text">
                Leave Min blank for &ldquo;less than&rdquo;, leave Max blank for &ldquo;greater than&rdquo;.
            </p>
        );
    }

    if (min && max) {
        const minNum = parseFloat(min);
        const maxNum = parseFloat(max);
        if (Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum >= maxNum) {
            return (
                <p className="mt-1.5 text-xs font-medium text-mld-danger">
                    Min must be less than Max.
                </p>
            );
        }
        return (
            <p className="mt-1.5 text-xs font-medium text-mld-warning">
                Normal: <span className="font-mono">{min}</span> &ndash;{' '}
                <span className="font-mono">{max}</span>
            </p>
        );
    }

    if (max) {
        return (
            <p className="mt-1.5 text-xs font-medium text-mld-warning">
                Normal: result &lt; <span className="font-mono">{max}</span>
            </p>
        );
    }

    return (
        <p className="mt-1.5 text-xs font-medium text-mld-warning">
            Normal: result &gt; <span className="font-mono">{min}</span>
        </p>
    );
}

// Discriminator selector option lists.  `null` is the "any / does not apply"
// sentinel — it maps to NULL in the database and means the override applies
// regardless of the patient's value for that field.
const SEX_OPTIONS: {value: RefRangeSex | null; label: string}[] = [
    {value: null, label: 'All'},
    {value: 'M', label: 'Male'},
    {value: 'F', label: 'Female'},
];

const MENOPAUSAL_OPTIONS: {value: RefRangeMenopausalStatus | null; label: string}[] = [
    {value: null, label: 'Any'},
    {value: 'pre', label: 'Pre'},
    {value: 'post', label: 'Post'},
];

const BIOTIN_OPTIONS: {value: boolean | null; label: string}[] = [
    {value: null, label: 'Any'},
    {value: true, label: 'On biotin'},
    {value: false, label: 'Off biotin'},
];

const CYCLE_PHASE_OPTIONS: {value: RefRangeCyclePhase | null; label: string}[] = [
    {value: null, label: 'Any'},
    {value: 'follicular', label: 'Follicular'},
    {value: 'luteal', label: 'Luteal'},
    {value: 'midcycle', label: 'Mid-cycle'},
];

const SEX_LABEL: Record<RefRangeSex, string> = {M: 'Male', F: 'Female'};
const MENOPAUSAL_LABEL: Record<RefRangeMenopausalStatus, string> = {pre: 'Pre-menopausal', post: 'Post-menopausal'};
const CYCLE_PHASE_LABEL: Record<RefRangeCyclePhase, string> = {
    follicular: 'Follicular',
    luteal: 'Luteal',
    midcycle: 'Mid-cycle',
};

/**
 * Segmented-control row used for each of the four demographic discriminators
 * in the Add/Edit modal.  Generic over the value type so we can share the
 * pattern across sex / menopausal / biotin / cycle_phase.
 */
function DiscriminatorRow<T extends string | boolean | null>(props: {
    label: string;
    options: ReadonlyArray<{value: T; label: string}>;
    value: T;
    onChange: (value: T) => void;
}) {
    return (
        <div className="grid grid-cols-[120px_1fr] items-center gap-3">
            <span className="text-xs font-medium text-mld-text-secondary">{props.label}</span>
            <div className="flex gap-1 rounded-lg border border-mld-panel-border bg-mld-menu-background p-0.5">
                {props.options.map((opt) => {
                    const isActive = opt.value === props.value;
                    return (
                        <button
                            key={String(opt.value)}
                            type="button"
                            onClick={() => props.onChange(opt.value)}
                            className={clsx(
                                'flex-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition',
                                isActive
                                    ? 'bg-white text-mld-primary shadow-sm'
                                    : 'text-mld-muted-text hover:text-mld-text-secondary'
                            )}>
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Build a human-readable summary of the demographic discriminators on a row.
 * Returns "All patients" when no discriminator is set.
 */
function summarizeAppliesTo(row: OrgReferenceRange): string {
    const parts: string[] = [];
    if (row.sex) parts.push(SEX_LABEL[row.sex]);
    if (row.menopausal_status) parts.push(MENOPAUSAL_LABEL[row.menopausal_status]);
    if (row.on_biotin === true) parts.push('On biotin');
    else if (row.on_biotin === false) parts.push('Off biotin');
    if (row.cycle_phase) parts.push(CYCLE_PHASE_LABEL[row.cycle_phase]);
    return parts.length === 0 ? 'All patients' : parts.join(' · ');
}

const ReferenceRangesManager: React.FC<Props> = ({orgId: orgIdOverride = null}) => {
    const {organization} = useTenant();
    const orgId = orgIdOverride ?? organization?.id ?? null;
    const queryClient = useQueryClient();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [testCode, setTestCode] = useState('');
    const [testName, setTestName] = useState('');
    const [rangeFields, setRangeFields] = useState<RangeFields>({...EMPTY_RANGE});
    const [notes, setNotes] = useState('');
    const [nameQuery, setNameQuery] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    // Demographic discriminators — null = "any" / applies regardless.
    const [sex, setSex] = useState<RefRangeSex | null>(null);
    const [menopausalStatus, setMenopausalStatus] = useState<RefRangeMenopausalStatus | null>(null);
    const [onBiotin, setOnBiotin] = useState<boolean | null>(null);
    const [cyclePhase, setCyclePhase] = useState<RefRangeCyclePhase | null>(null);

    const queryKey = ['admin', 'reference-ranges', orgId];

    const {data: ranges = [], isLoading} = useQuery({
        queryKey,
        queryFn: () => AdminService.listReferenceRanges(orgId as number),
        enabled: Boolean(orgId),
        staleTime: 60_000,
    });

    const {data: assays = []} = useQuery({
        queryKey: ['admin', 'assay-catalog'],
        queryFn: () => AdminService.getAssayCatalog(),
        staleTime: 5 * 60_000,
    });

    const assayByName = useMemo(() => {
        const map = new Map<string, AssayCatalogItem>();
        for (const a of assays) map.set(a.name, a);
        return map;
    }, [assays]);

    // Only actual lab tests — exclude calculated values, ratios, and bundled panels
    const testAssays = useMemo(
        () => assays.filter((a) => !a.is_calculated && !a.is_bundled_panel),
        [assays]
    );

    const filteredByName = useMemo(() => {
        if (!nameQuery) return testAssays;
        const q = nameQuery.toLowerCase();
        return testAssays.filter((a) => a.name.toLowerCase().includes(q));
    }, [testAssays, nameQuery]);

    // Resolve assay code to store — prefer elis_test_code for results matching.
    // Even though the code input is no longer surfaced in the UI, we still
    // persist `test_code` alongside `test_name` because the dashboard
    // ref-range JOIN matches code-first for accuracy.
    const resolveTestCode = (assay: AssayCatalogItem) => assay.elis_test_code || assay.code;

    const handleNameSelect = useCallback(
        (assay: AssayCatalogItem | null) => {
            if (!assay) return;
            setTestCode(resolveTestCode(assay));
            setTestName(assay.name);
            setNameQuery('');
        },
        []
    );

    const createMutation = useMutation({
        mutationFn: (input: OrgReferenceRangeInput) => AdminService.createReferenceRange(orgId!, input),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Reference range created');
            closeModal();
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.error ?? 'Failed to create reference range';
            toast.error(msg);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({id, input}: {id: number; input: Partial<OrgReferenceRangeInput>}) =>
            AdminService.updateReferenceRange(orgId!, id, input),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Reference range updated');
            closeModal();
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.error ?? 'Failed to update reference range';
            toast.error(msg);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => AdminService.deleteReferenceRange(orgId!, id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Reference range deleted');
            setDeleteConfirmId(null);
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.error ?? 'Failed to delete reference range';
            toast.error(msg);
        },
    });

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditingId(null);
        setTestCode('');
        setTestName('');
        setRangeFields({...EMPTY_RANGE});
        setNotes('');
        setNameQuery('');
        setSex(null);
        setMenopausalStatus(null);
        setOnBiotin(null);
        setCyclePhase(null);
    }, []);

    const openCreate = useCallback(() => {
        setEditingId(null);
        setTestCode('');
        setTestName('');
        setRangeFields({...EMPTY_RANGE});
        setNotes('');
        setSex(null);
        setMenopausalStatus(null);
        setOnBiotin(null);
        setCyclePhase(null);
        setModalOpen(true);
    }, []);

    const openEdit = useCallback((row: OrgReferenceRange) => {
        setEditingId(row.id);
        setTestCode(row.test_code ?? '');
        setTestName(row.test_name ?? '');
        setRangeFields(parseRefRange(row.ref_range));
        setNotes(row.notes ?? '');
        setSex(row.sex);
        setMenopausalStatus(row.menopausal_status);
        setOnBiotin(row.on_biotin);
        setCyclePhase(row.cycle_phase);
        setModalOpen(true);
    }, []);

    const handleSubmit = useCallback(() => {
        const code = testCode.trim() || null;
        const name = testName.trim() || null;

        if (!code && !name) {
            toast.error('Select a test from the dropdown');
            return;
        }

        const refRange = buildRefRange(rangeFields);
        if (!refRange) {
            toast.error('Enter at least a Min or a Max value');
            return;
        }
        if (rangeFields.min.trim() && rangeFields.max.trim()) {
            const minNum = parseFloat(rangeFields.min);
            const maxNum = parseFloat(rangeFields.max);
            if (Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum >= maxNum) {
                toast.error('Min must be less than Max');
                return;
            }
        }

        const input: OrgReferenceRangeInput = {
            test_code: code,
            test_name: name,
            ref_range: refRange,
            notes: notes.trim() || null,
            sex,
            menopausal_status: menopausalStatus,
            on_biotin: onBiotin,
            cycle_phase: cyclePhase,
        };

        if (editingId != null) {
            updateMutation.mutate({id: editingId, input});
        } else {
            createMutation.mutate(input);
        }
    }, [
        testCode,
        testName,
        rangeFields,
        notes,
        editingId,
        createMutation,
        updateMutation,
        sex,
        menopausalStatus,
        onBiotin,
        cyclePhase,
    ]);

    const isSaving = createMutation.isPending || updateMutation.isPending;

    // Find the assay record that matches the currently selected name so the
    // Combobox highlights it on open and on edit.
    const selectedAssayByName = testName ? assayByName.get(testName) ?? null : null;

    if (!orgId) {
        return (
            <div className="py-8 text-center text-sm text-mld-muted-text">
                No organization selected.
            </div>
        );
    }

    return (
        <div className="mt-4">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-mld-menu-text">Reference Range Overrides</h3>
                    <p className="text-xs text-mld-muted-text">
                        Custom ranges override the lab-provided defaults for biomarker display.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openCreate}
                    className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-mld-primary-700">
                    <Plus className="h-4 w-4" />
                    Add Range
                </button>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-mld-muted-text" />
                </div>
            ) : ranges.length === 0 ? (
                <div className="rounded-lg border border-dashed border-mld-panel-border py-10 text-center text-sm text-mld-muted-text">
                    No reference range overrides configured.
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-mld-panel-border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-mld-panel-border bg-mld-menu-background text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                <th className="px-4 py-2.5">Test Code</th>
                                <th className="px-4 py-2.5">Test Name</th>
                                <th className="px-4 py-2.5">Range</th>
                                <th className="px-4 py-2.5">Applies To</th>
                                <th className="px-4 py-2.5">Notes</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranges.map((row) => (
                                <tr key={row.id} className="border-b border-mld-panel-border/30 last:border-0">
                                    <td className="px-4 py-2.5 font-mono text-xs text-mld-text-secondary">
                                        {row.test_code || <span className="text-mld-muted-text">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-mld-text-secondary">
                                        {row.test_name || <span className="text-mld-muted-text">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-2.5 font-mono text-xs font-medium text-mld-primary">
                                        {row.ref_range}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-text-secondary">
                                        {summarizeAppliesTo(row)}
                                    </td>
                                    <td className="max-w-[200px] truncate px-4 py-2.5 text-xs text-mld-muted-text">
                                        {row.notes || <span className="text-mld-muted-text">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => openEdit(row)}
                                                className="rounded p-1 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary"
                                                title="Edit">
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDeleteConfirmId(row.id)}
                                                className="rounded p-1 text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger"
                                                title="Delete">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create / Edit Modal */}
            <Transition appear show={modalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="border-b border-mld-panel-border px-6 py-4">
                                        <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                            {editingId != null ? 'Edit Reference Range' : 'Add Reference Range'}
                                        </Dialog.Title>
                                        <p className="text-xs text-mld-muted-text">
                                            Override the lab-provided reference range for a specific test.
                                        </p>
                                    </div>

                                    <div className="space-y-4 px-6 py-5">
                                        {/* Test Name Combobox (the underlying test_code is auto-populated
                                            from the chosen assay so DB matching stays code-first). */}
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Test
                                            </label>
                                            <Combobox value={selectedAssayByName} onChange={handleNameSelect}>
                                                <div className="relative">
                                                    <ComboboxInput
                                                        displayValue={(a: AssayCatalogItem | null) =>
                                                            a ? a.name : testName
                                                        }
                                                        onChange={(e) => {
                                                            setNameQuery(e.target.value);
                                                            setTestName(e.target.value);
                                                        }}
                                                        placeholder="Search by name..."
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                    />
                                                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-mld-muted-text" />
                                                    <ComboboxOptions className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-mld-panel-border bg-white py-1 text-sm shadow-lg">
                                                        {filteredByName.length === 0 ? (
                                                            <div className="px-3 py-2 text-mld-muted-text">No matches</div>
                                                        ) : (
                                                            filteredByName.map((a) => (
                                                                <ComboboxOption
                                                                    key={a.id}
                                                                    value={a}
                                                                    className="cursor-pointer px-3 py-1.5 data-[focus]:bg-mld-primary/10">
                                                                    {a.name}
                                                                    <span className="ml-2 font-mono text-xs text-mld-muted-text">
                                                                        {a.elis_test_code || a.code}
                                                                    </span>
                                                                </ComboboxOption>
                                                            ))
                                                        )}
                                                    </ComboboxOptions>
                                                </div>
                                            </Combobox>
                                        </div>

                                        {/* Reference Range — two inputs, implicit format with live preview. */}
                                        <div>
                                            <label className="mb-2 block text-xs font-medium text-mld-text-secondary">
                                                Reference Range
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={rangeFields.min}
                                                    onChange={(e) =>
                                                        setRangeFields((f) => ({...f, min: e.target.value}))
                                                    }
                                                    placeholder="Min"
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-center text-sm font-mono focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                                <span className="text-xs font-medium text-mld-muted-text">to</span>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={rangeFields.max}
                                                    onChange={(e) =>
                                                        setRangeFields((f) => ({...f, max: e.target.value}))
                                                    }
                                                    placeholder="Max"
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-center text-sm font-mono focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                            </div>
                                            <RangePreview fields={rangeFields} />
                                        </div>

                                        {/* Applies To — demographic discriminators */}
                                        <div>
                                            <label className="mb-2 block text-xs font-medium text-mld-text-secondary">
                                                Applies To{' '}
                                                <span className="font-normal text-mld-muted-text">
                                                    (leave as &ldquo;Any&rdquo; to apply to all patients)
                                                </span>
                                            </label>

                                            <div className="space-y-3">
                                                <DiscriminatorRow
                                                    label="Sex"
                                                    options={SEX_OPTIONS}
                                                    value={sex}
                                                    onChange={(next) => {
                                                        setSex(next);
                                                        // Menopausal status and cycle phase don't apply to males —
                                                        // reset them so an admin can't save a logically-impossible
                                                        // rule (e.g. sex=M + menopausal=post).  Female / All keep
                                                        // whatever was already chosen.
                                                        if (next === 'M') {
                                                            setMenopausalStatus(null);
                                                            setCyclePhase(null);
                                                        }
                                                    }}
                                                />
                                                {sex !== 'M' && (
                                                    <DiscriminatorRow
                                                        label="Menopausal status"
                                                        options={MENOPAUSAL_OPTIONS}
                                                        value={menopausalStatus}
                                                        onChange={setMenopausalStatus}
                                                    />
                                                )}
                                                <DiscriminatorRow
                                                    label="Biotin"
                                                    options={BIOTIN_OPTIONS}
                                                    value={onBiotin}
                                                    onChange={setOnBiotin}
                                                />
                                                {sex !== 'M' && (
                                                    <DiscriminatorRow
                                                        label="Cycle phase"
                                                        options={CYCLE_PHASE_OPTIONS}
                                                        value={cyclePhase}
                                                        onChange={setCyclePhase}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Notes */}
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Notes <span className="font-normal text-mld-muted-text">(optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Internal note about this override"
                                                className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={isSaving}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                            {editingId != null ? 'Save Changes' : 'Create'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Delete Confirmation Modal */}
            <Transition appear show={deleteConfirmId != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setDeleteConfirmId(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-sm rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="px-6 py-5">
                                        <Dialog.Title className="text-base font-semibold text-mld-menu-text">
                                            Delete Reference Range
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-mld-muted-text">
                                            This will revert the test to its lab-provided default range. Continue?
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setDeleteConfirmId(null)}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteConfirmId != null && deleteMutation.mutate(deleteConfirmId)}
                                            disabled={deleteMutation.isPending}
                                            className={clsx(
                                                'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60',
                                                'bg-mld-danger/50 hover:bg-mld-danger'
                                            )}>
                                            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                            Delete
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default ReferenceRangesManager;
